import { Button, message, Select, Space, Spin } from 'antd';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import Card from '../../Card';
import useAPIQuery from '../../api/useAPIQuery';
import ErrorResult from '../../components/ErrorResult';
import Form from '../../components/Form';
import PageContent from '../../components/PageContent';
import RichEditor, {
  quillGetHTMLString,
  fixUnorderedListInQuill,
} from '../../components/RichEditor/RichEditor';
import useDescriptionMutation from '../../hooks/useDescriptionMutation';
import useDiscardChangesModal from '../../hooks/useDiscardChangesModal';
import useTypedNavigate from '../../hooks/useTypedNavigate';
import useTypedParams from '../../hooks/useTypedParams';
import { withRouteChange } from '../../services/APM';
import { SinapseResult } from '../../sinapse/api';
import { SinapseTag } from '../../sinapse/components/SinapseTag';
import { COMPETENCY_MAP } from '../../sinapse/constants/actions';
import { aiPurple } from '../../sinapse/constants/colors';
import {
  OpportunityDescriptionGenerator,
  useGenerateOpportunityDescriptionStatus,
} from '../../sinapse/generate_opportunity_description';
import { SparkIcon } from '../../sinapse/icons/spark';
import { JobPostingId } from '../../types';
import { handleJobPostingDescriptionLengthValidator } from '../../validators/jobPostingDescriptionValidator';

interface DescriptionFormValues {
  description: string;
  competencies?: number[];
}

const DescriptionForm = () => {
  const { t } = useTranslation();

  const competenciesOptions = Object.entries(COMPETENCY_MAP).map(([key, value]) => ({
    label: value,
    value: Number(key),
    key,
  }));

  const { tenant, id } = useTypedParams({ tenant: String, id: JobPostingId });
  const [searchParams] = useSearchParams();

  const jobPosting = useAPIQuery(`/job-postings/${id}/`);
  const config = useAPIQuery('/configs/tenant-configurations/');

  const [form] = Form.useForm();
  const descriptionValue = Form.useWatch('description', form);

  const [setIsDirty] = useDiscardChangesModal(form);

  const navigate = useTypedNavigate();
  const [leavePage, setLeavePage] = useState<boolean>(false);
  const [descriptionItemDisabled, setDescriptionItemDisabled] = useState(false);
  const generateOpportunityDescriptionStatus = useGenerateOpportunityDescriptionStatus(id);

  const setResultValuesForGeneratedOpportunityDescription = (result: SinapseResult) => {
    if (result?.text) form.setFieldsValue({ description: result?.text.replaceAll('\n', '<br>') });
    if (result?.competencies) form.setFieldsValue({ competencies: result?.competencies });
    setIsDirty(true);
  };

  const queryClient = useQueryClient();

  const mutation = useDescriptionMutation({
    onSuccess: () => {
      setIsDirty(false);
      message.success(t('Vaga atualizada'));

      if (leavePage) {
        return navigate({
          pathname: '/:tenant/opportunities/:id/',
          params: {
            tenant,
            id,
          },
        });
      }

      if (searchParams.get('new')) {
        return navigate({
          pathname: '/:tenant/opportunities/:id/change/stages/',
          params: {
            tenant,
            id,
            new: 1,
          },
        });
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries(`/job-postings/${id}/`);
    },
  });

  return (
    <ErrorResult error={jobPosting.error}>
      <Helmet>
        <title>
          {t('Descrição da vaga')} | {jobPosting.data?.name || '...'} |{' '}
          {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <PageContent>
        <Spin spinning={jobPosting.isLoading}>
          <Form<DescriptionFormValues>
            form={form}
            layout="vertical"
            validateMessages={{
              required: t('Campo obrigatório.'),
            }}
            initialValues={{
              description: quillGetHTMLString(jobPosting.data?.description || '') || '',
              competencies: jobPosting.data?.competencies || [],
            }}
            onFinish={(variables) => {
              mutation.mutate({
                ...variables,
                jobPostingId: id,
                description: fixUnorderedListInQuill(variables.description),
              });
            }}
            onValuesChange={() => setIsDirty(form.isFieldsTouched())}
          >
            {config.data?.enableGenerateJobpostingDescription && (
              <OpportunityDescriptionGenerator
                enabled
                sourceId={id}
                setResult={setResultValuesForGeneratedOpportunityDescription}
                textValue={descriptionValue}
                setIsDisabledInput={setDescriptionItemDisabled}
              />
            )}
            <Space direction="vertical" size={16}>
              <Card>
                <Form.Item
                  name="description"
                  label={t('Descrição da vaga')}
                  required
                  rules={[
                    { required: true },
                    {
                      validator: handleJobPostingDescriptionLengthValidator,
                    },
                  ]}
                >
                  <RichEditor data-testid="rich-editor-input" readOnly={descriptionItemDisabled} />
                </Form.Item>
                {config.data?.enableGenerateJobpostingDescription &&
                  config.data?.enableCompetencyGeneration && (
                    <Form.Item name="competencies" label={t('Competências da Vaga')}>
                      <Select
                        allowClear={false}
                        disabled
                        mode="multiple"
                        options={competenciesOptions}
                        placeholder={t('Selecione as competências')}
                        style={{ width: '100%' }}
                        tagRender={(props) => (
                          <SinapseTag>
                            <>
                              {props.label}{' '}
                              <SparkIcon
                                style={{
                                  fontSize: '0.9em',
                                  color: aiPurple,
                                  marginLeft: '0.5em',
                                }}
                              />
                            </>
                          </SinapseTag>
                        )}
                      />
                    </Form.Item>
                  )}
              </Card>
              <Form.Footer>
                <Form.FooterEnd>
                  <Button
                    onClick={() =>
                      navigate({ pathname: '/:tenant/opportunities/:id/', params: { tenant, id } })
                    }
                  >
                    <Trans>Cancelar</Trans>
                  </Button>
                  <Button
                    htmlType="submit"
                    loading={mutation.isLoading}
                    disabled={generateOpportunityDescriptionStatus === 'pending'}
                    onClick={() => {
                      searchParams.delete('new');
                      setLeavePage(true);
                    }}
                  >
                    <Trans>Salvar e Sair</Trans>
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={mutation.isLoading}
                    disabled={generateOpportunityDescriptionStatus === 'pending'}
                  >
                    {searchParams.get('new') ? t('Salvar e continuar') : t('Salvar')}
                  </Button>
                </Form.FooterEnd>
              </Form.Footer>
            </Space>
          </Form>
          <div style={{ height: 80 }} />
        </Spin>
      </PageContent>
    </ErrorResult>
  );
};

export default withRouteChange()(DescriptionForm);
